/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ColumnHeader } from '../core/models/column-header.model';
import { WorksheetExportRequest } from '../core/models/worksheet-export-request.model';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {

  // File constants
  private readonly fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  private readonly fileExtension = '.xlsx';
  protected baseEndpoint: string;
  public readonly excelSheetUrl: string;

  constructor(protected readonly configService: ConfigService,
              protected readonly http: HttpClient) {
    this.baseEndpoint = `${this.configService.baseApiUrl}excel`;
    this.excelSheetUrl = this.baseEndpoint + '/sheet-names';
  }

  /**
   * Download excel file.
   * @param buffer is a data object.
   * @param fileName name of the file.
   */
  private saveExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {type: this.fileType});
    FileSaver.saveAs(data, fileName);
  }

  /**
   * Add extension to file.
   * @param buffer is a data object.
   * @param filename name of the file.
   */
  public exportToExcelFromServer(buffer: any, filename: string): void {
    this.saveExcelFile(buffer, filename + this.fileExtension);
  }

  /**
   * Get the Excel file from the export request data
   * @param worksheetExportRequestBody - The request body to send to the Excel API service
   */
  public transformToExcel(worksheetExportRequestBody: WorksheetExportRequest): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-Disposition'] = `attachment; filename=${worksheetExportRequestBody.fileName}.xlsx`;
    return this.http.post(this.baseEndpoint + '/export',
      worksheetExportRequestBody, {headers, responseType: 'blob'}).pipe(catchError(error => throwError(error)));
  }

  /**
   * Create the request body for the excel export service call
   * @param fileName name that the Excel file will be named
   * @param data the data containing the row data to fill excel sheets with
   * @param sheetList the list of sheets
   * @param numTypeFields a list of sheets with columns that are cell type number
   */
  public createExportRequestBody(fileName: string,
                                 data: Map<string, any>,
                                 sheetList: [string, ColumnHeader[]][],
                                 numTypeFields?: Map<string, string[]>): WorksheetExportRequest {
    const req = new WorksheetExportRequest();
    const headers = new Map<string, string[]>();
    const fieldNames = new Map<string, string[]>();
    sheetList.forEach(tab => {
      const sheetName = tab[0];
      const columnHeaders = tab[1];
      headers[sheetName] = columnHeaders.map(header => header.label);
      fieldNames[sheetName] = columnHeaders.map(header => header.name);
    });
    req.fileName = fileName;
    req.headers = headers;
    req.fieldNames = fieldNames;
    req.data = data;
    req.numTypeFields = numTypeFields;
    return req;
  }
}
